import { Transition } from "@headlessui/react"
import { Form, Head } from "@inertiajs/react"
import { Mail } from "lucide-react"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import { mailerSettingsPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import type { MailerSetting } from "@/types"

interface MailerSettingsShowProps {
  mailer_settings: MailerSetting
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Mailer Settings", href: mailerSettingsPath() },
]

export default function MailerSettingsShow({
  mailer_settings,
}: MailerSettingsShowProps) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Mailer Settings" />

      <div className="flex-1 md:max-w-2xl">
        <section className="max-w-xl space-y-12">
          <div className="p-6">
            <div className="space-y-6">
              <HeadingSmall
                title="Email Configuration"
                description="Configure how Alfred sends email notifications"
              />

              <Form
                method="put"
                action={mailerSettingsPath()}
                options={{ preserveScroll: true }}
                className="space-y-6"
              >
                {({ processing, errors, recentlySuccessful }) => (
                  <>
                    <div className="grid gap-2">
                      <Label htmlFor="from_name">From Name</Label>
                      <Input
                        id="from_name"
                        name="mailer_setting[from_name]"
                        type="text"
                        defaultValue={mailer_settings.from_name}
                        placeholder="Alfred"
                      />
                      <p className="text-muted-foreground text-xs">
                        The name that appears in the "From" field of emails
                      </p>
                      <InputError
                        messages={errors["mailer_setting.from_name"]}
                      />
                    </div>

                    <div className="grid gap-2">
                      <Label htmlFor="from_email">From Email</Label>
                      <div className="relative">
                        <Mail className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                        <Input
                          id="from_email"
                          name="mailer_setting[from_email]"
                          type="email"
                          defaultValue={mailer_settings.from_email}
                          placeholder="bots@you.digital"
                          className="pl-10"
                        />
                      </div>
                      <p className="text-muted-foreground text-xs">
                        The email address used to send notifications
                      </p>
                      <InputError
                        messages={errors["mailer_setting.from_email"]}
                      />
                    </div>

                    <div className="flex items-center gap-4">
                      <Button disabled={processing}>Save Settings</Button>

                      <Transition
                        show={recentlySuccessful}
                        enter="transition ease-in-out"
                        enterFrom="opacity-0"
                        leave="transition ease-in-out"
                        leaveTo="opacity-0"
                      >
                        <p className="text-sm text-neutral-600">Saved</p>
                      </Transition>
                    </div>
                  </>
                )}
              </Form>
            </div>
          </div>
        </section>
      </div>
    </AppLayout>
  )
}
